import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { AButton } from 'ui'
import { CContactFormModal } from 'doctors/components/organisms/c-contact-form-modal'
import { CContactFormFieldsV2 } from 'doctors/components/organisms/c-contact-form-fields-v2'
import useSettings from 'doctors/hooks/use-settings'
import * as CONST from 'ui/constants/index'
import { useSiteInfo } from 'shared'
import { CraftDoctor, CraftLocation } from 'doctors/craft-types'

type Props = {
  doctor?: CraftDoctor
  location?: CraftLocation
  name?: string
  step: number
  setStep: Dispatch<SetStateAction<number>>
}

const defaultProps = {
  doctor: undefined,
  location: undefined,
  name: '',
  step: 1,
  setStep: () => {},
}

type SubmittedFormData = {
  lastName: string
  email: string
  phone: string
  message: string
  isSubscribed: boolean
}

const CContactForm = ({ doctor, location, name, step, setStep }: Props) => {
  const siteInfo = useSiteInfo()
  const router = useRouter()
  const settings = useSettings()
  const { t } = useTranslation('common')

  const [showModal, setShowModal] = useState<boolean>(false)
  const [submittedFormData, setSubmittedFormData] = useState<SubmittedFormData>({
    lastName: '',
    email: '',
    phone: '',
    message: '',
    isSubscribed: false,
  })

  useEffect(() => {
    setStep(1)
  }, [])

  const cancelForm = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    const url = `/${doctor ? 'doctors' : 'locations'}/${router.query.slug}/about`
    router.push(url, undefined, { shallow: true })

    setStep(1)
  }

  const handleMainFormSuccess = (data: SubmittedFormData) => {
    setSubmittedFormData(data)
    setStep(2)
  }

  const handleMultipleDoctors = () => {
    setShowModal(true)
  }

  return (
    <div className="font-body">
      {step === 1 && (
        <>
          <div className="text-base font-semibold leading-7 text-gray-800 text-center lg:text-left">
            {doctor?.customFormVerbiage || t('form-info')}
          </div>

          <CContactFormFieldsV2
            doctor={doctor}
            location={location}
            onCancel={cancelForm}
            onSuccess={handleMainFormSuccess}
          />

          <div className="text-sm text-gray-700 font-semibold leading-4 mt-8 text-center lg:text-left">
            {siteInfo.isGE && (
              <>
                <div
                  className="text-gray-700 w-full [&>p>a]:underline [&>p>a]:text-primary-teal [&>p>a:hover]:text-primary-dark-teal"
                  dangerouslySetInnerHTML={{ __html: t('form-note') }}
                />
                <br />
              </>
            )}
            {t('form-data-provide')}
            <br />
            {t('staar-more-info')}{' '}
            <a
              href={t('privacy-policy-url') ?? ''}
              className={classNames({
                'text-pink-500 hover:text-pink-600':
                  siteInfo?.layout === CONST.SITE_LAYOUT.EVO,
                'text-primary-teal hover:text-primary-dark-teal':
                  siteInfo?.layout === CONST.SITE_LAYOUT.ICL,
              })}
            >
              {t('privacy-policy')}
            </a>{' '}
            {t('and')}{' '}
            <a
              href={t('terms-of-use-url') ?? ''}
              className={classNames({
                'text-pink-500 hover:text-pink-600':
                  siteInfo?.layout === CONST.SITE_LAYOUT.EVO,
                'text-primary-teal hover:text-primary-dark-teal':
                  siteInfo?.layout === CONST.SITE_LAYOUT.ICL,
              })}
            >
              {t('terms-of-use')}
            </a>
            .
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="text-base font-semibold leading-7 text-gray-400 text-center lg:text-left">
            {t('message-submitted-info')}{' '}
            <span className="text-gray-800"> {name}. </span>
          </div>

          {!!doctor && settings.contact.modal && (
            <>
              <div className="border border-gray-400 my-10" />
              <h2 className="lg:text-left font-body font-light mt-4 text-2xl md:text-3xl text-gray-700 text-center">
                {t('contact-multiple-heading')}
              </h2>
              <div className="text-base font-semibold leading-7 mb-4 text-gray-400 text-center lg:text-left">
                {t('contact-multiple-subheading')}
              </div>
              <div className="lg:flex">
                <AButton
                  size="lg"
                  style="outline"
                  title={t('contact-multiple-button') ?? ''}
                  type="button"
                  onClick={handleMultipleDoctors}
                >
                  <>{t('contact-multiple-button')}</>
                </AButton>
              </div>

              <CContactFormModal
                data={submittedFormData}
                open={showModal}
                onClose={() => setShowModal(false)}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

CContactForm.defaultProps = defaultProps

export { CContactForm }